import { useState, useEffect, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(isSameOrAfter);

export interface PendingOrderFilters {
  immediate: any[];
  onDeck: any[];
  today: any[];
  future: any[];
  onDeckIntervalMinutes: number;
  todayIntervalHours: number;
}

export const usePendingOrderFilters = (pendingOrders: any[]): PendingOrderFilters => {
  const [immediateCutoff, setImmediateCutoff] = useState<Dayjs>(dayjs());

  const onDeckIntervalMinutes = 60;
  const todayIntervalHours = 24;

  // Refresh the immediate cutoff time every 10 seconds to make sure
  // immediate and future pending orders are updated as time moves on.
  useEffect(() => {
    const interval = setInterval(() => {
      setImmediateCutoff(dayjs());
    }, 10 * 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const filters = useMemo(() => {
    const onDeckCutoff = immediateCutoff.clone().add(onDeckIntervalMinutes, "minutes");
    const todayCutoff = immediateCutoff.clone().add(todayIntervalHours, "hours");

    const immediate = pendingOrders.filter((order) => {
      if (order.display_after) {
        return dayjs(order.display_after).isBefore(immediateCutoff);
      }
      // Order without display_after is immediate.
      return true;
    });

    const onDeck = pendingOrders.filter((order) => {
      if (order.display_after) {
        const da = dayjs(order.display_after);
        return da.isSameOrAfter(immediateCutoff) && da.isBefore(onDeckCutoff);
      }
      return false;
    });

    const today = pendingOrders.filter((order) => {
      if (order.display_after) {
        const da = dayjs(order.display_after);
        return da.isSameOrAfter(onDeckCutoff) && da.isBefore(todayCutoff);
      }
      return false;
    });

    const future = pendingOrders.filter((order) => {
      if (order.display_after) {
        return dayjs(order.display_after).isSameOrAfter(todayCutoff);
      }
      return false;
    });

    return { immediate, onDeck, today, future };
  }, [pendingOrders, immediateCutoff, onDeckIntervalMinutes, todayIntervalHours]);

  return {
    ...filters,
    onDeckIntervalMinutes,
    todayIntervalHours,
  };
};
